import {Helmet} from '@dr.pogodin/react-helmet';
import {openConfirmDialog, openDeleteDialog} from 'platform/components';
import {match} from 'ts-pattern';

import {isArray} from 'ramda-adjunct';

import {
  useDeleteTireRimMutation,
  usePostTireRimSetActiveMutation,
  usePostTireRimSetInactiveMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {DmsDataGrid, handleApiError} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

export function TireInventoryRim() {
  const navigate = useNavigate();

  const [deleteTireRim] = useDeleteTireRimMutation();
  const [postTireRimSetActive] = usePostTireRimSetActiveMutation();
  const [postTireRimSetInactive] = usePostTireRimSetInactiveMutation();

  const actionCallback: ActionCallback = ({actionKey, rowId, refreshData}) => {
    const id = isArray(rowId) ? rowId[0] : rowId;

    match(actionKey)
      .with('edit', 'redirectDetail', () =>
        navigate(composePath(settingsRoutes.tireRimSizeDetail, {params: {id}}))
      )
      .with('setAsInactive', () =>
        openConfirmDialog({
          onConfirm: () =>
            postTireRimSetInactive({tireRimId: id})
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError),
        })
      )
      .with('setAsActive', () =>
        openConfirmDialog({
          onConfirm: () =>
            postTireRimSetActive({tireRimId: id})
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError),
        })
      )
      .with('delete', () =>
        openDeleteDialog({
          onConfirm: () =>
            deleteTireRim({tireRimId: id})
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError),
        })
      );
  };

  return (
    <>
      <Helmet title={i18n.t('entity.tiresInventory.labels.tireRimSize')} />
      <SettingsTemplate
        header={{
          title: i18n.t('entity.tiresInventory.labels.tireRimSize'),
          actions: [
            {
              type: 'button',
              title: i18n.t('entity.tiresInventory.labels.newTireRimSize'),
              onClick: () => navigate(settingsRoutes.tireRimSizeCreate),
            },
          ],
        }}
        description={i18n.t('entity.tiresInventory.labels.tireRimSizeDescription')}
        data-testid={testIds.settings.tireRimSize('template')}
      >
        <DmsDataGrid
          gridCode="tire-rim"
          actionCallback={actionCallback}
          data-testid={testIds.settings.tireRimSize('datagrid')}
        />
      </SettingsTemplate>
    </>
  );
}
